import { useEffect, useMemo, useState } from "react";
import { ChevronRight, Loader2, Search, X } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { useSite } from "@/hooks/use-site";
import {
  loadSearchIndex,
  searchDocuments,
  type SearchDocument,
  type SearchDocumentType,
} from "@/lib/search";
import { localizeUrl } from "@/lib/site";

type SearchPageProps = {
  initialQuery?: string;
};

export function SearchPage({ initialQuery = "" }: SearchPageProps) {
  const { messages, site, href } = useSite();
  const home = localizeUrl("/", site.locale, href);
  const [documents, setDocuments] = useState<SearchDocument[]>([]);
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);

    loadSearchIndex(site.locale)
      .then((index) => {
        if (cancelled) return;
        setDocuments(index);
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [site.locale]);

  const trimmedQuery = query.trim();
  const results = useMemo(
    () => searchDocuments(documents, trimmedQuery, 20),
    [documents, trimmedQuery],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      updateQueryParam(trimmedQuery);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [trimmedQuery]);

  const typeLabels: Record<SearchDocumentType, string> = {
    page: messages.searchTypePage,
    product: messages.searchTypeProduct,
    blog: messages.searchTypeBlog,
    support: messages.searchTypeSupport,
    legal: messages.searchTypeLegal,
    app: messages.searchTypeApp,
  };

  function clearSearch() {
    setQuery("");
  }

  return (
    <SiteLayout>
      <div className="border-b border-border/60 bg-background/60">
        <nav
          aria-label={messages.breadcrumbLabel}
          className="mx-auto flex max-w-7xl items-center gap-1.5 px-6 py-3 text-xs text-muted-foreground"
        >
          <a href={home} className="hover:text-foreground">
            {messages.tv6UltraBreadcrumbHome}
          </a>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{messages.searchEyebrow}</span>
        </nav>
      </div>

      <section className="border-b border-border/60 bg-muted/10">
        <div className="mx-auto max-w-3xl px-6 py-14 lg:py-20">
          <p className="text-sm font-medium text-primary">{messages.searchEyebrow}</p>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight sm:text-5xl">
            {messages.searchHeading}
          </h1>
          <p className="mt-4 text-base leading-7 text-muted-foreground">{messages.searchIntro}</p>

          <div role="search" className="mt-8">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/60" />
              <input
                type="text"
                role="searchbox"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={messages.searchPlaceholder}
                className="h-11 w-full rounded-xl border border-border bg-card pl-10 pr-10 text-sm text-foreground placeholder:text-muted-foreground/60 focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                autoComplete="off"
                autoFocus
              />
              {query && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground/60 hover:text-foreground"
                  aria-label={messages.searchClear}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-3xl px-6 py-10">
          {loading ? (
            <div className="flex items-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              {messages.searchLoading}
            </div>
          ) : failed ? (
            <EmptyState title={messages.searchErrorTitle} body={messages.searchErrorBody} />
          ) : !trimmedQuery ? (
            <EmptyState title={messages.searchNoQueryTitle} body={messages.searchNoQueryBody} />
          ) : results.length === 0 ? (
            <EmptyState
              title={messages.searchNoResultsTitle}
              body={messages.searchNoResultsBody.replace("{query}", trimmedQuery)}
            />
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">
                {messages.searchResultsCount
                  .replace("{count}", String(results.length))
                  .replace("{query}", trimmedQuery)}
              </p>
              <div className="mt-5 divide-y divide-border rounded-xl border border-border bg-card">
                {results.map((result) => (
                  <a
                    key={result.id}
                    href={localizeUrl(result.path, site.locale, href)}
                    className="block px-5 py-5 transition-colors hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-foreground/90">
                        {typeLabels[result.type]}
                      </span>
                      <span className="text-xs text-muted-foreground">{result.path}</span>
                    </div>
                    <h2 className="mt-2 text-lg font-semibold leading-snug text-foreground">
                      {result.title}
                    </h2>
                    {result.excerpt && (
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">
                        {result.excerpt}
                      </p>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="py-14 text-center">
      <p className="text-base font-medium text-foreground">{title}</p>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-muted-foreground">{body}</p>
    </div>
  );
}

function updateQueryParam(query: string) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (query) {
    url.searchParams.set("q", query);
  } else {
    url.searchParams.delete("q");
  }
  window.history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
}
